import type { FC, KeyboardEvent, MouseEvent, ReactElement, TouchEvent } from 'react';
import React, { cloneElement } from 'react';

import { isSafari } from '../../../../utils/devices/device';
import PremiumLabel from '../../../labels/PremiumLabel/PremiumLabel';
import TextLabel from '../../../labels/TextLabel/TextLabel';
import { Size, Variant } from './types';
import {
  CenterContainer,
  EllipsisBasicParagraph,
  LeftContainer,
  LeftExternalComponent,
  PremiumWrapper,
  RightComponentWrapper,
  RightExternalComponent,
  StyledBasicParagraph,
  TextLabelWrapper,
  Wrapper,
} from './Item.styled';

export interface ItemProps {
  /**
   * Component located on the left
   */
  LeftComponent?: ReactElement;
  /**
   * Component located on the right
   */
  RightComponent?: ReactElement;
  /**
   * Property used for testing purposes
   */
  dataCy?: string;
  /**
   * Indicates if the premium icon will be displayed
   */
  hasPremiumLabel?: boolean;
  /**
   * Indicates if the premium icon will be in color
   */
  isPremium?: boolean;
  /**
   * Function for slide action. It is executed when you click on the item and when you press
   * Enter or Space when the focus is on the item.
   */
  onItemAction?: (e: MouseEvent | KeyboardEvent) => void;
  /**
   * Function that is executed when you hover on the item.
   */
  onMouseEnter?: (e: MouseEvent) => void;
  /**
   * Function that is executed when you tap on the item.
   */
  onTouchStart?: (e: TouchEvent) => void;
  /**
   * Function that is executed when touch is complete.
   */
  onTouchEnd?: (e: TouchEvent) => void;
  /**
   * There are two sizes of List.Item: `Item.Size.MEDIUM`, `Item.Size.SMALL`.
   */
  size?: Size;
  /**
   * Text that displays on the item.
   */
  text: string;
  /**
   * Indicates if the item has been hovered over.
   */
  isHovered?: boolean;
  /**
   * Indicates of the RightComponent when hovering over the item.
   */
  rightComponentHoverColor?: string;
  /**
   * Says whether the button is disabled or not.
   */
  disabled?: boolean;
  /**
   * Text for the label. The label displays if there is labelText.
   */
  labelText?: string;
  /**
   * Indicates if the paragraph has ellipsis or not.
   */
  withoutEllipsis?: boolean;
  /**
   * There are two variants of the item: `Item.Variant.DEFAULT`, `Item.Variant.DARK`.
   */
  variant?: Variant;
  /**
   * Indicates the role of the list item. Typically, it is `option` for list items.
   */
  role?: string;
  /**
   * Indicates to a screen reader if the item is selected.
   */
  ariaSelected?: boolean;
}

const Item: FC<ItemProps> & { Size: typeof Size; Variant: typeof Variant } = ({
  dataCy,
  disabled = false,
  hasPremiumLabel = false,
  isHovered = false,
  isPremium = false,
  labelText,
  LeftComponent,
  onItemAction,
  onMouseEnter,
  onTouchEnd,
  onTouchStart,
  RightComponent,
  rightComponentHoverColor,
  size = Size.SMALL,
  text,
  withoutEllipsis = false,
  variant = Variant.LIGHT,
  role,
  ariaSelected,
}) => {
  const ClonedLeftComponent = (
    props: (Partial<unknown> & React.Attributes) | undefined,
  ) => cloneElement(LeftComponent as ReactElement, props);

  const ClonedRightComponent = (
    props: (Partial<unknown> & React.Attributes) | undefined,
  ) => cloneElement(RightComponent as ReactElement, props);

  const handleItemAction = onItemAction;
  const handleOnMouseEnter = disabled ? undefined : onMouseEnter;
  const handleOnTouchStart = disabled ? undefined : onTouchStart;
  const handleOnTouchEnd = disabled ? undefined : onTouchEnd;

  const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (handleItemAction) {
      if (['Enter', ' '].includes(e.key)) {
        e.preventDefault();

        handleItemAction(e);
      }
    }
  };

  const isDarkMode = variant === Variant.DARK;

  return (
    <Wrapper
      $isClickable={!!handleItemAction}
      $isDisabled={disabled}
      $isHovered={isHovered}
      $rightComponentHoverColor={rightComponentHoverColor}
      $size={size}
      $withoutEllipsis={withoutEllipsis}
      $isDarkMode={isDarkMode}
      data-cy={dataCy}
      onClick={handleItemAction}
      onKeyDown={handleOnKeyDown}
      onMouseEnter={handleOnMouseEnter}
      onTouchEnd={handleOnTouchEnd}
      onTouchStart={handleOnTouchStart}
      tabIndex={isSafari ? undefined : 0}
      role={role}
      aria-selected={ariaSelected}
    >
      {!!LeftComponent && (
        <LeftContainer>
          <LeftExternalComponent as={ClonedLeftComponent} />
        </LeftContainer>
      )}

      <CenterContainer>
        {withoutEllipsis ? (
          <StyledBasicParagraph $isDarkMode={isDarkMode}>{text}</StyledBasicParagraph>
        ) : (
          <EllipsisBasicParagraph $isDarkMode={isDarkMode}>{text}</EllipsisBasicParagraph>
        )}

        {!!hasPremiumLabel && (
          <PremiumWrapper>
            <PremiumLabel isPremium={isPremium} />
          </PremiumWrapper>
        )}

        {labelText && (
          <TextLabelWrapper>
            <TextLabel text={labelText} />
          </TextLabelWrapper>
        )}
      </CenterContainer>

      {!!RightComponent && (
        <RightComponentWrapper>
          <RightExternalComponent as={ClonedRightComponent} />
        </RightComponentWrapper>
      )}
    </Wrapper>
  );
};

Item.Size = Size;
Item.Variant = Variant;

export default Item;
